import { useState, useEffect } from 'preact/hooks';
import { useAppState } from '../lib/state';
import { Modal } from '../components/Modal';
import { ButtonPrimary } from '../components/ButtonPrimary';
import { ButtonSecondary } from '../components/ButtonSecondary';
import {
  buildExportPayload,
  serializePlainExport,
  encryptExport,
  downloadAsFile,
  exportFilename,
  recordExportInDb,
} from '../lib/export';
import { parseExport, applyImport } from '../lib/import';
import { shouldShowBackupReminder } from '../lib/backup';
import { openDb, getSettings } from '../lib/db';

export function Settings() {
  const { state, dispatch } = useAppState();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showEncryptPassphrase, setShowEncryptPassphrase] = useState(false);
  const [encryptPassphrase, setEncryptPassphrase] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showBackupReminder, setShowBackupReminder] = useState(false);
  const [lastBackupAt, setLastBackupAt] = useState<string | null>(null);

  // Load backup state from IDB on mount
  useEffect(() => {
    (async () => {
      const db = await openDb();
      const settings = await getSettings(db);
      db.close();
      if (settings) {
        setLastBackupAt(settings.last_backup_export_at);
        setShowBackupReminder(
          shouldShowBackupReminder(
            {
              last_backup_export_at: settings.last_backup_export_at,
              contributions_since_last_backup: settings.contributions_since_last_backup,
            },
            new Date().toISOString()
          )
        );
      }
    })();
  }, []);

  async function handleExport(mode: 'plain' | 'encrypted') {
    try {
      const payload = buildExportPayload(state);
      const plain = serializePlainExport(payload);

      let content: string;
      let filename: string;
      if (mode === 'plain') {
        content = plain;
        filename = exportFilename(false);
      } else {
        if (!encryptPassphrase) {
          setShowEncryptPassphrase(true);
          return;
        }
        const encrypted = await encryptExport(plain, encryptPassphrase);
        content = JSON.stringify(encrypted, null, 2);
        filename = exportFilename(true);
        setShowEncryptPassphrase(false);
        setEncryptPassphrase('');
      }

      downloadAsFile(content, filename, 'application/json');

      // Record export in IDB (reset backup counter)
      const db = await openDb();
      await recordExportInDb(db);
      db.close();
      setLastBackupAt(new Date().toISOString());
      setShowBackupReminder(false);
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async function handleImportFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setImportError(null);
    setImportSuccess(false);

    const text = await file.text();
    const parsed = parseExport(text);
    if (!parsed.ok) {
      setImportError(parsed.error);
      return;
    }

    const db = await openDb();
    const result = await applyImport(db, parsed.payload);
    db.close();

    if (!result.ok) {
      setImportError(result.error);
      return;
    }

    dispatch({ type: 'HYDRATE', state: result.state });
    setImportSuccess(true);
  }

  function handleReset() {
    dispatch({ type: 'RESET' });
    setShowResetConfirm(false);
    setLastBackupAt(null);
    setShowBackupReminder(false);
  }

  function formatBackupDate(iso: string | null): string {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  return (
    <div class="max-w-md mx-auto px-4 py-6 space-y-6">
      <h1 class="text-xl font-bold text-hudson-navy">Settings</h1>

      <section class="space-y-3">
        <h2 class="text-sm uppercase tracking-wider text-body/70 font-semibold">Your data</h2>

        {showBackupReminder && (
          <div role="alert" class="text-sm bg-grain-gold/20 border border-grain-gold rounded-lg px-3 py-2 flex items-start gap-2">
            <span aria-hidden="true" class="mt-0.5">⚠</span>
            <span>Time for a backup — your data lives on this device only.</span>
          </div>
        )}

        <p class="text-xs text-body/70">
          Last backup: <strong>{formatBackupDate(lastBackupAt)}</strong>
        </p>

        <p class="text-xs text-body/70 leading-relaxed">
          Export saves all your data to a file on your device. Import it here anytime —
          on this device or a new one — to restore everything exactly as you left it.
        </p>
        <p class="text-xs text-body/70 leading-relaxed">
          Every backup ships from a hash-verified, formula-certified, audit-grade
          calculation engine. The CRA constants version is recorded with each export.
        </p>
        <ButtonPrimary onClick={() => handleExport('plain')}>
          Export JSON backup
        </ButtonPrimary>
        <ButtonSecondary onClick={() => { setShowEncryptPassphrase(true); }}>
          Export encrypted backup
        </ButtonSecondary>
        <ButtonSecondary onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.json';
          input.onchange = handleImportFile;
          input.click();
        }}>
          Import from backup
        </ButtonSecondary>

        {importError && (
          <p role="alert" class="text-sm text-alert-red bg-white border border-alert-red rounded-lg px-3 py-2">
            {importError}
          </p>
        )}
        {importSuccess && (
          <p role="status" class="text-sm text-success-green bg-white border border-success-green rounded-lg px-3 py-2">
            Data imported successfully.
          </p>
        )}
      </section>

      <section class="space-y-2">
        <h2 class="text-sm uppercase tracking-wider text-body/70 font-semibold">Privacy</h2>
        <a
          href="/privacy"
          class="block text-sm text-hudson-navy underline focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded"
        >
          Privacy policy →
        </a>
      </section>

      <section class="space-y-1">
        <h2 class="text-sm uppercase tracking-wider text-body/70 font-semibold">Version</h2>
        <p class="text-sm text-body/70">Constants: {state.constantsVersion}</p>
        <p class="text-sm text-body/70">Verified: {state.constantsVerifiedAt}</p>
      </section>

      <section class="space-y-3 border-t border-card-border pt-4">
        <h2 class="text-sm uppercase tracking-wider text-alert-red font-semibold">Danger zone</h2>
        <button
          type="button"
          onClick={() => setShowResetConfirm(true)}
          class="text-sm text-alert-red border border-alert-red rounded-lg px-4 py-2 hover:bg-white focus:outline-none focus:ring-2 focus:ring-alert-red"
        >
          Reset all data
        </button>
      </section>

      {/* Reset confirmation modal */}
      <Modal open={showResetConfirm} title="Reset all data?" onClose={() => setShowResetConfirm(false)}>
        <div class="space-y-4">
          <p class="text-sm text-body">
            This will erase all your data — wizard answers, contributions, and withdrawals.
            This cannot be undone.
          </p>
          <p class="text-sm bg-cream border border-grain-gold rounded px-3 py-2 flex items-center gap-2">
            <span aria-hidden="true">⚠</span>
            <span>Have you exported a backup? Your data lives on this device only.</span>
          </p>
          <div class="flex gap-3">
            <ButtonSecondary onClick={() => setShowResetConfirm(false)}>Cancel</ButtonSecondary>
            <button
              type="button"
              onClick={handleReset}
              class="flex-1 py-2.5 px-4 bg-alert-red text-white font-semibold rounded-lg hover:bg-alert-red/90 focus:outline-none focus:ring-2 focus:ring-alert-red focus:ring-offset-2"
            >
              Reset
            </button>
          </div>
        </div>
      </Modal>

      {/* Encrypted export passphrase modal */}
      <Modal
        open={showEncryptPassphrase}
        title="Set export passphrase"
        onClose={() => { setShowEncryptPassphrase(false); setEncryptPassphrase(''); }}
      >
        <div class="space-y-4">
          <p class="text-sm text-body">
            Your backup will be encrypted with AES-256. If you forget this passphrase,
            the backup is permanently unrecoverable.
          </p>
          <input
            type="password"
            value={encryptPassphrase}
            onInput={(e) => setEncryptPassphrase((e.target as HTMLInputElement).value)}
            placeholder="Enter passphrase"
            class="w-full border border-card-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-hudson-navy"
            autoFocus
          />
          <div class="flex gap-3">
            <ButtonSecondary onClick={() => { setShowEncryptPassphrase(false); setEncryptPassphrase(''); }}>
              Cancel
            </ButtonSecondary>
            <ButtonPrimary
              onClick={() => handleExport('encrypted')}
              disabled={!encryptPassphrase}
            >
              Encrypt &amp; download
            </ButtonPrimary>
          </div>
        </div>
      </Modal>
    </div>
  );
}
